import { FluxErrorCodes } from "@buildwithflux/constants";
import { pcbLayoutNestedNodeDelimiter, PcbNodeTypes, } from "@buildwithflux/models";
import { toPascalCase } from "@buildwithflux/shared";
import { Clipper } from "../../../clipper";
import { ElementHelper } from "../../../elements";
import { FluxBaseError } from "../../../flux_error";
import { getDisplayed5DigitChangeId } from "../../../helpers";
import { getAncestorUids, getTopLevelLayoutOfNode } from "../../../nodes";
import { areOldProperties, getDefaultValueProperty, getDesignatorPrefixFromProperties } from "../../../property";
import { createShapeFromBakedRules, createShapeFromPolygonBakedRules } from "../../../shapes";
import { getAbsoluteDocumentUrl, getAbsoluteDocumentUrlFromUid, getAbsoluteProfileUrlForDocumentOwner, } from "../../../url";
import { RuleParser } from "../../layout_engine/rule_parsing_and_compilation";
import { stringifyPropertyValue } from "../ExpressionEvaluationHelpers";
export function getProjectExpressionEvaluationContext(documentData) {
    // Evaluating properties is a bit of a circular issue because you need the context to create the context
    // So I implemented a bit of a hack that should work for the most part
    // Open to better ideas
    // TODO: owner url is calculated incorrectly for enterprise-owned documents!
    const projectContext = {
        name: documentData.name,
        description: documentData.description,
        // write un-evaluated values first
        properties: extractEvaluationContextProperties(ElementHelper.extractPropertiesWithoutEvaluation(documentData.properties ?? {})),
        url: getAbsoluteDocumentUrl(documentData),
        slug: documentData.slug,
        owner: {
            handle: documentData.owner_handle,
            url: getAbsoluteProfileUrlForDocumentOwner(documentData),
        },
        changeHistory: {
            currentChangeUid: documentData.latestActionRecordUid || "",
            currentChangeShortUid: documentData.latestActionRecordUid
                ? getDisplayed5DigitChangeId(documentData.latestActionRecordUid) || ""
                : "",
        },
    };
    const context = {
        project: projectContext,
    };
    // Now do a first pass on evaluations
    projectContext.properties = extractEvaluationContextProperties(extractPropertiesWithExpressionEvaluation(documentData.properties ?? {}, context));
    // Need to run this a second time so that properties that are dependent on other properties can be evaluated
    projectContext.properties = extractEvaluationContextProperties(extractPropertiesWithExpressionEvaluation(documentData.properties ?? {}, context));
    return projectContext;
}
export function getNodeExpressionEvaluationContext(node, flattenedRules, documentData, logger) {
    const linkedElementData = getParentElementData(node, documentData, logger);
    // HACK: some legacy parts have no pcbNodeRuleSet
    // See TEMP_patchMissingButRequiredDocumentData
    const rules = Object.values(node.pcbNodeRuleSet || {});
    let nodeContext = {
        uid: node.uid,
        parentUid: node.parentUid,
        type: node.type,
        designator: node.name,
        rules: rules.reduce((accumulator, rule) => {
            return {
                ...accumulator,
                [rule.key]: rule.value,
            };
        }, {}),
        activeRules: rules.reduce((accumulator, rule) => {
            return rule.disabled
                ? {
                    ...accumulator,
                }
                : {
                    ...accumulator,
                    [rule.key]: rule.value,
                };
        }, {}),
        bakedRules: node.bakedRules,
    };
    // Question: Should these params be added as baked rules instead? They're not really rules but it would
    // make it easier to pass into the expression engine
    if (node.type === PcbNodeTypes.net) {
        nodeContext.current = node.current;
        nodeContext.desiredTemperatureRise = node.desiredTemperatureRise;
        nodeContext.impedancePair = node.impedancePair;
        nodeContext.impedance = node.impedance;
    }
    if (node.type === PcbNodeTypes.routeSegment) {
        const parentNode = documentData.pcbLayoutNodes[node.parentUid];
        if (parentNode && parentNode.type === PcbNodeTypes.net) {
            nodeContext.current = parentNode.current;
            nodeContext.desiredTemperatureRise = parentNode.desiredTemperatureRise;
            nodeContext.impedance = parentNode.impedance;
            nodeContext.impedancePair = parentNode.impedancePair;
        }
    }
    // Add `area` to the node context if it's a layout or polygon node
    let shape = undefined;
    if (node.type === PcbNodeTypes.layout && node.bakedRules) {
        shape = createShapeFromBakedRules(node.bakedRules, 0);
    }
    else if (node.type === PcbNodeTypes.polygon) {
        const parsedBakedRules = new RuleParser(flattenedRules, undefined, PcbNodeTypes.polygon).parseRules([
            "polygonShape",
            "size",
            "cornerRadius",
        ]);
        shape = createShapeFromPolygonBakedRules(parsedBakedRules);
    }
    if (shape && shape.curves.length) {
        const clipperShape = Clipper.shape([shape.getPoints(64)]);
        const area = Math.abs(clipperShape.totalArea());
        nodeContext.area = area;
    }
    if (linkedElementData) {
        nodeContext = {
            ...nodeContext,
            element: {
                designator: linkedElementData.label,
                defaultPropertyValue: linkedElementData.defaultPropertyValue,
                properties: extractEvaluationContextProperties(linkedElementData.properties ?? {}),
                part: {
                    name: linkedElementData.partName || linkedElementData.part_version_data_cache?.name,
                    url: linkedElementData.document_import_uid &&
                        getAbsoluteDocumentUrlFromUid(linkedElementData.document_import_uid),
                },
            },
        };
    }
    return nodeContext;
}
export function getNodeLayoutExpressionEvaluationContext(node, documentData, logger) {
    const layout = getTopLevelLayoutOfNode(documentData.pcbLayoutNodes, node.uid);
    if (layout) {
        return getNodeExpressionEvaluationContext(layout, {}, documentData, logger);
    }
    return undefined;
}
function getSubjectExpressionEvaluationContext(extractedElementData, linkedElementNodeData) {
    let subjectContext = {
        designator: extractedElementData.label,
        defaultPropertyValue: extractedElementData.defaultPropertyValue,
        properties: extractEvaluationContextProperties(extractedElementData.properties ?? {}),
        part: {
            name: extractedElementData.partName || extractedElementData.part_version_data_cache?.name,
            url: extractedElementData.document_import_uid &&
                getAbsoluteDocumentUrlFromUid(extractedElementData.document_import_uid),
        },
    };
    if (linkedElementNodeData) {
        const rules = Object.values(linkedElementNodeData.pcbNodeRuleSet);
        subjectContext = {
            ...subjectContext,
            node: {
                uid: linkedElementNodeData.uid,
                parentUid: linkedElementNodeData.parentUid,
                type: linkedElementNodeData.type,
                designator: linkedElementNodeData.name,
                rules: rules.reduce((accumulator, rule) => {
                    return {
                        ...accumulator,
                        [rule.key]: rule.value,
                    };
                }, {}),
                activeRules: rules.reduce((accumulator, rule) => {
                    return rule.disabled
                        ? {
                            ...accumulator,
                        }
                        : {
                            ...accumulator,
                            [rule.key]: rule.value,
                        };
                }, {}),
                bakedRules: linkedElementNodeData.bakedRules,
            },
        };
    }
    return subjectContext;
}
export function createSubjectExpressionContext(elementData, documentData, 
// Subjects don't necessarily have a linked pcb node, so this is optional
linkedPcbLayoutElementNode) {
    const expressionEvaluationContext = {
        project: getProjectExpressionEvaluationContext(documentData),
    };
    expressionEvaluationContext.this = getSubjectExpressionContext(expressionEvaluationContext, elementData, linkedPcbLayoutElementNode);
    return expressionEvaluationContext;
}
export function getSubjectExpressionContext(expressionEvaluationContext, elementData, linkedPcbLayoutElementNode) {
    let subjectExpressionEvaluationContext;
    if (linkedPcbLayoutElementNode?.type === PcbNodeTypes.element) {
        subjectExpressionEvaluationContext = getSubjectExpressionEvaluationContext(extractElementDataWithExpressionEvaluation(elementData, expressionEvaluationContext), linkedPcbLayoutElementNode);
        // need to do this twice to be able to evaluate previous values
        subjectExpressionEvaluationContext = getSubjectExpressionEvaluationContext(extractElementDataWithExpressionEvaluation(elementData, expressionEvaluationContext), linkedPcbLayoutElementNode);
    }
    return subjectExpressionEvaluationContext;
}
export function createPcbLayoutNodeExpressionContext(documentData, logger, pcbLayoutNode, layoutNode) {
    const expressionEvaluationContext = {
        project: getProjectExpressionEvaluationContext(documentData),
    };
    if (pcbLayoutNode) {
        expressionEvaluationContext.this = getNodeExpressionEvaluationContext(pcbLayoutNode, pcbLayoutNode.flattenedRules ?? {}, documentData, logger);
    }
    if (layoutNode) {
        expressionEvaluationContext.layout = getNodeExpressionEvaluationContext(layoutNode, layoutNode.flattenedRules ?? {}, documentData, logger);
    }
    return expressionEvaluationContext;
}
export function extractEvaluationContextProperties(properties) {
    return (Object.values(properties)
        // there is corrupt property data with undefined names for some reason...so need to filter
        // See IExtractedPropertyDataMap_old for possible reason.
        .filter((property) => property.name)
        .reduce((accumulator, propertyData) => {
        return {
            ...accumulator,
            [toPascalCase(propertyData.name)]: propertyData.value,
        };
    }, {}));
}
function getParentElementData(pcbLayoutElementNode, documentData, logger) {
    // is element node
    const parentElement = documentData.elements[pcbLayoutElementNode.uid];
    if (parentElement) {
        const context = createSubjectExpressionContext(parentElement, documentData);
        return extractElementDataWithExpressionEvaluation(parentElement, context);
    }
    // TODO: deouble check ME!!! and cache needed???
    const ancestorUids = getAncestorUids(documentData.pcbLayoutNodes, pcbLayoutElementNode);
    const parentElementNodeUid = ancestorUids.find((ancestorUid) => documentData.pcbLayoutNodes[ancestorUid]?.type === PcbNodeTypes.element);
    if (parentElementNodeUid && parentElementNodeUid in documentData.elements) {
        //is root layout component child node
        const parentElement = documentData.elements[parentElementNodeUid];
        const context = createSubjectExpressionContext(parentElement, documentData);
        return extractElementDataWithExpressionEvaluation(parentElement, context);
    }
    else if (parentElementNodeUid) {
        //is sub layout component child node
        // TODO: assert(ancestorElementUid === path[0]), not sure what historical reasons make us do this check instead
        //       of starting a search from the root and walking down the path normally
        // This is top-down, so it's the distant ancestor, not the proximate one - don't use it as the parent because
        // that'll break when embedded two levels deep
        // TODO: deouble check ME!!! and cache needed???
        const ancestorUids = getAncestorUids(documentData.pcbLayoutNodes, pcbLayoutElementNode, true);
        // NOTE: find last ancestor
        ancestorUids.reverse();
        const ancestorElementUid = ancestorUids.find((ancestorUid) => documentData.pcbLayoutNodes[ancestorUid]?.type === PcbNodeTypes.element);
        if (!ancestorElementUid || !(ancestorElementUid in documentData.elements)) {
            logNoSuchElementError(ancestorElementUid, documentData.pcbLayoutNodes, logger);
            return undefined;
        }
        const path = parentElementNodeUid.split(pcbLayoutNestedNodeDelimiter);
        let element = documentData.elements?.[ancestorElementUid];
        path.shift(); // Because first part of path is ancestorElementUid anyway
        // Walk down the path
        let errorSeen = false;
        for (const part of path) {
            element = element?.part_version_data_cache?.extracted_element_cache?.[part];
            if (!element && !errorSeen) {
                logNoSuchElementError(part, documentData.pcbLayoutNodes, logger);
                errorSeen = true;
                return undefined;
            }
        }
        // HACK: this is needed to add a unit to sublayout elements
        // defaultPropertyValue which is missing the unit for unknown reasons.
        // It should probably be fixed upstream somehow. See
        // extractElementDataWithExpressionEvaluation.
        const defaultProperty = getDefaultValueProperty(element.properties ?? {}, true);
        if (defaultProperty) {
            return {
                ...element,
                defaultPropertyValue: stringifyPropertyValue(defaultProperty),
            };
        }
        return element;
    }
    else {
        // is root footprint child node
        const designatorPrefix = getDesignatorPrefixFromProperties(documentData.properties ?? {});
        const defaultProperty = getDefaultValueProperty(documentData.properties ?? {});
        return {
            partName: documentData.name,
            document_import_uid: documentData.uid,
            label: designatorPrefix ? `${designatorPrefix}#` : `<Designator Placeholder>`,
            defaultPropertyValue: defaultProperty?.value.toString() || `<Default Property Placeholder>`,
            // TODO: do these need to be evaluated maybe?
            properties: ElementHelper.extractPropertiesWithoutEvaluation(documentData.properties ?? {}),
        };
    }
}
class NodeMissingElementError extends FluxBaseError {
    constructor(node, elementUid, options = {}) {
        super(FluxErrorCodes.NodeMissingElementError, `${node ? node.type : "Unknown"} node "${elementUid}" does not have a corresponding Element`, options);
    }
}
function logNoSuchElementError(elementUid, pcbLayoutNodes, logger) {
    const node = elementUid ? pcbLayoutNodes[elementUid] : undefined;
    const err = new NodeMissingElementError(node, elementUid);
    if ("captureError" in logger) {
        logger.captureError(err, { errorKey: `NodeMissingElementError:${elementUid}` });
    }
    else {
        logger.error(err);
    }
}
function extractElementDataWithExpressionEvaluation(elementData, context) {
    // Extract element data needed for sub layouts to work.
    const defaultProperty = getDefaultValueProperty(elementData.properties ?? {});
    return {
        label: elementData.label,
        defaultPropertyValue: defaultProperty?.value ? stringifyPropertyValue(defaultProperty, context) : undefined,
        properties: extractPropertiesWithExpressionEvaluation(elementData.properties ?? {}, context),
        document_import_uid: elementData.part_version_data_cache?.document_import_uid,
        part_version_data_cache: {
            name: elementData.part_version_data_cache?.name ?? "",
            extracted_element_cache: elementData.part_version_data_cache?.extracted_element_cache ?? {},
            pcbLayoutRuleSets: elementData.part_version_data_cache?.pcbLayoutRuleSets ?? {},
            part_uid: elementData.part_version_data_cache?.part_uid ?? "",
            assets: elementData.part_version_data_cache?.assets ?? {},
        },
    };
}
function extractPropertiesWithExpressionEvaluation(properties, context) {
    const extractedProperties = {};
    if (areOldProperties(properties)) {
        // Skipping, since it's not clear what we should be doing here for this unexpected data shape.
        // TODO: add tests for this case when seen in the wild
    }
    else {
        for (const [uid, propertyData] of Object.entries(properties)) {
            extractedProperties[uid] = {
                name: propertyData.name,
                value: stringifyPropertyValue(propertyData, context),
            };
        }
    }
    return extractedProperties;
}
//# sourceMappingURL=ExpressionContextHelpers.js.map